import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
  ContentImagesDto,
  ExtranetProductImageDto,
  MultilingualKeyDto,
} from '@pharmac-extranet/extranet-api'
import { MediaQuery, useBreakpoint, useLanguage } from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacTruncate } from '@pharmac/ui'
import { HTMLAttributes, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

type ExtranetCardProps = PropsWithChildren &
  Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
    image?: ContentImagesDto
    productImage?: ExtranetProductImageDto
    title: MultilingualKeyDto
    onClick?: () => void
    date?: string
    category?: MultilingualKeyDto
    width?: string
    headerHeight?: string
    buttonText?: string
    cardBody?: React.ReactNode[] | React.ReactNode | undefined
  }

const CardWidthStyle = (props: { width?: string }) => {
  let percentage = '33%'

  if (props.width && props.width.match(/[\d]{1,2}%/)) {
    percentage = props.width
  }

  return css`
    width: calc(${percentage} - var(--spacing-small));
  `
}

const ExtranetCardRoot = styled.div<{ width?: string }>`
  border-radius: var(--extranet-card-border-radius);
  background-color: var(--color-accent-2);

  ${CardWidthStyle};

  transition: var(--transition-cubic-bezier);

  ${MediaQuery.maxTablet} {
    width: 100%;
    height: auto;

    margin-bottom: var(--spacing-medium);

    .encard__header {
      height: auto;

      .encard__header--image {
        width: 100%;
        height: auto;
      }
    }
  }

  :hover {
    cursor: pointer;

    box-shadow: 2px 3px 10px 1px rgba(0, 0, 0, 0.12);
    transform: translate3d(-2px, -4px, 0px);

    background-color: var(--extranet-light-purple);

    .encard__header--image {
      opacity: 0.6;
    }

    .encard__header-button {
      opacity: 1;
    }
  }
`

const ExtranetCardHeader = styled.div<{ height?: string }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: ${(props) => (props.height ? props.height : '150px')};
  border-radius: var(--extranet-card-border-radius) var(--extranet-card-border-radius) 0 0;
`

const ExtranetCardImage = styled.img`
  width: 100%;
  object-fit: cover
  height: 100%;
  opacity: 1;
  transition: var(--transition-cubic-bezier);
`

const ExtranetCardButton = styled.div`
  position: absolute;

  opacity: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  transition: var(--transition-cubic-bezier);

  button {
    border-radius: var(--button-primary-border-radius);
    background-color: var(--extranet-light-purple);
    color: var(--color-primary);
    padding: var(--button-primary-padding);
    line-height: 22px;

    font-size: 18px;
    font-weight: 600;

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    box-shadow: var(--shadow-small);

    svg {
      height: 18px;
      margin-left: var(--spacing-small);
      transform: translateY(1px);
    }
  }
`

const ExtranetCardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  padding: var(--spacing-tiny) var(--spacing-medium);

  font-family: var(--font-family-secondary);
  font-weight: 300;
`

const ExtranetCardMeta = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: var(--spacing-small) 0;
  font-size: calc(var(--typo-text-medium-size) + 1px);
`

const ExtranetCardDate = styled.div`
  line-height: 100%;
  padding: 0 var(--spacing-tiny);
`

const ExtranetCardCategory = styled.div`
  display: inline-block;
  margin-right: var(--spacing-small);

  line-height: 100%;
`

const ExtranetCardTitle = styled.div<{ minDesktop?: boolean }>`
  width: 100%;
  height: ${(props) => (props.minDesktop ? '57.6px' : '')};
  overflow-wrap: break-word;

  font-weight: 700;
  font-size: calc(var(--typo-text-medium-size) + 2px);

  padding: var(--spacing-small) 0;
  color: var(--color-accent-7);
`

export const ExtranetCard = ({
  image,
  productImage,
  title,
  onClick,
  date,
  category,
  width,
  headerHeight,
  buttonText,
  cardBody,
  ...props
}: ExtranetCardProps) => {
  const { t } = useTranslation()
  const { key } = useLanguage()
  const { minDesktop, maxTablet } = useBreakpoint()

  const imageUrl =
    image?.mobile ||
    image?.desktop ||
    image?.desktopLarge ||
    productImage?.desktopLargeUrl ||
    productImage?.desktopUrl ||
    productImage?.desktopWideUrl ||
    productImage?.mobileUrl

  const textTruncate = (() => {
    if (minDesktop) return 50
    if (maxTablet) return 255
    return 30
  })()

  return (
    <ExtranetCardRoot
      width={width}
      onClick={onClick}
      className="encard"
      {...props}
    >
      <ExtranetCardHeader
        height={headerHeight}
        className="encard__header"
      >
        <ExtranetCardImage
          className="encard__header--image"
          src={imageUrl}
        />
        <ExtranetCardButton className="encard__header-button">
          <button className="encard__header-button--button">
            {buttonText ? buttonText : t('extranetCard.buttonLabel', { ns: 'extranet' })}{' '}
            <PharmacIcon icon="chevronRight" />
          </button>
        </ExtranetCardButton>
      </ExtranetCardHeader>
      <ExtranetCardBody className="encard__body">
        <ExtranetCardMeta className="encard__meta">
          <ExtranetCardCategory className="encard__meta--category">
            {!!category && key(category)}
          </ExtranetCardCategory>
          <ExtranetCardDate className="encard__meta--date">{date}</ExtranetCardDate>
        </ExtranetCardMeta>
        <ExtranetCardTitle
          className="encard__title"
          minDesktop={minDesktop}
        >
          {PharmacTruncate(key(title), textTruncate)}
        </ExtranetCardTitle>
        {cardBody}
      </ExtranetCardBody>
    </ExtranetCardRoot>
  )
}
